// Compact view toolbar in the top bar. Layer SHOW/HIDE lives in the left tree only; this
// bar is just view aids (plan view, basemap, 3D transparency, wireframe, fly-to, go-to).
import type { Viewer } from "cesium";
import { api } from "../api";
import type { AssetRef, LayerManager } from "../cesium/layers";
import { flyToLonLat, savedBaseLayer, setBaseLayer, type BaseLayerKind } from "../cesium/viewer";
import { MGA_ZONES, defaultZoneForLon, epsgForZone, type MgaZone } from "../cesium/mga";
import { $activeProjectId, $mgaZone, $projectLon } from "../store";
import { toast } from "./toast";

/** Friendly short label for a long DEM filename. */
function demLabel(name: string): string {
  const n = name.toUpperCase();
  if (n.includes("WESTERN_HUB") || n.includes("WYLOO") || n.includes("COBRA")) return "Wyloo 1m";
  if (n.includes("ELIWANA")) return "Eliwana 1m";
  if (n.includes("LANDGATE") || n.includes("10M")) return "Regional 10m";
  return name.length > 22 ? name.slice(0, 20) + "…" : name;
}

export function mountViewTools(
  container: HTMLElement,
  layers: LayerManager,
  viewer: Viewer,
  _showAll: (dim: "2D" | "3D", on: boolean) => Promise<void>, // kept for call-site compat
  onPlanToggle: (on: boolean) => void,
): void {
  const bar = document.createElement("div");
  bar.className = "toolbar";

  // Plan view: lock top-down ortho and RAG-colour the site by sign-off status.
  const plan = document.createElement("button");
  plan.className = "tool-btn plan-btn";
  plan.textContent = "▦ Plan view";
  plan.title = "2D plan: top-down, layers coloured green=signed off / red=flagged";
  let planOn = false;
  plan.addEventListener("click", () => {
    planOn = !planOn;
    plan.classList.toggle("active", planOn);
    layers.topDown(planOn);
    onPlanToggle(planOn);
  });

  // Basemap switcher: satellite (default) or street map.
  const base = document.createElement("select");
  base.title = "Basemap";
  for (const [v, label] of [["sat", "Satellite"], ["map", "Street map"]] as const) {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = label;
    base.append(o);
  }
  base.value = savedBaseLayer();
  base.addEventListener("change", () => setBaseLayer(viewer, base.value as BaseLayerKind));

  // Terrain DEM picker: Cesium runs one ground terrain at a time, so the project's DEMs
  // (Wyloo 1m / Eliwana 1m / Regional 10m) are switched here. Defaults to the largest.
  const terrainSel = document.createElement("select");
  terrainSel.title = "Ground terrain DEM (one active at a time)";
  let terrains: { id: number; name: string; web_path: string }[] = [];
  let activeTerrain: AssetRef | null = null;
  const tref = (t: { id: number; web_path: string }): AssetRef => ({
    id: t.id, kind: "terrain", dimension: "3D", web_path: t.web_path,
  });
  const activateTerrain = (t: { id: number; web_path: string }) => {
    void layers.setVisible(tref(t), true);
    activeTerrain = tref(t);
  };
  async function refreshTerrains(): Promise<void> {
    const pid = $activeProjectId.get();
    terrainSel.innerHTML = "";
    activeTerrain = null;
    if (pid == null) return;
    const data = await api.terrains(pid).catch(() => ({ terrains: [] }));
    terrains = data.terrains;
    for (const t of terrains) {
      const o = document.createElement("option");
      o.value = String(t.id);
      o.textContent = demLabel(t.name);
      o.title = t.name;
      terrainSel.append(o);
    }
    const off = document.createElement("option");
    off.value = "";
    off.textContent = "Ground off";
    terrainSel.append(off);
    if (terrains.length) {
      // Default to the high-res Wyloo site DEM (not the broad low-res regional one).
      const def = terrains.find((t) => demLabel(t.name) === "Wyloo 1m") ?? terrains[0];
      activateTerrain(def);
      terrainSel.value = String(def.id);
    }
  }
  terrainSel.addEventListener("change", () => {
    const id = terrainSel.value;
    if (!id) {
      if (activeTerrain) void layers.setVisible(activeTerrain, false);
      activeTerrain = null;
      return;
    }
    const t = terrains.find((x) => String(x.id) === id);
    if (t) activateTerrain(t);
  });
  $activeProjectId.subscribe(() => void refreshTerrains());

  // Grid CRS for the cursor coordinate readout. The zone belongs to the PROJECT, not the
  // user or the cursor - SOE Kangwinan works in MGA zone 54, the Pilbara projects (Mindy,
  // Nullagine, Wyloo) in zone 50 - so the choice is remembered per project. Deliberately
  // not inferred from the cursor's longitude: coordinates must stay in the project's zone
  // even when the cursor wanders over a zone boundary.
  const crsSel = document.createElement("select");
  crsSel.title = "Grid used by the cursor coordinate readout (top-left of the map)";
  for (const z of MGA_ZONES) {
    const o = document.createElement("option");
    o.value = String(z.zone);
    o.textContent = `MGA ${z.zone}`;
    o.title = `${z.label} (EPSG:${z.epsg})`;
    crsSel.append(o);
  }
  const crsKey = (pid: number | null) => `geaar.crs.${pid ?? "none"}`;
  crsSel.addEventListener("change", () => {
    const zone = (Number(crsSel.value) === 54 ? 54 : 50) as MgaZone;
    localStorage.setItem(crsKey($activeProjectId.get()), String(zone));
    $mgaZone.set(zone);
  });
  // An explicit choice wins and sticks. Otherwise default from where the project's data
  // actually is, so SOE Kangwinan opens on zone 54 and the Pilbara projects on 50 with
  // nothing to configure - including for projects that don't exist yet.
  const applyZone = (): void => {
    const saved = localStorage.getItem(crsKey($activeProjectId.get()));
    const lon = $projectLon.get();
    const zone: MgaZone =
      saved === "54" || saved === "50"
        ? (Number(saved) as MgaZone)
        : lon != null
          ? defaultZoneForLon(lon)
          : 50;
    crsSel.value = String(zone);
    $mgaZone.set(zone);
  };
  $activeProjectId.subscribe(applyZone);
  $projectLon.subscribe(applyZone); // assets load after the project id changes

  // 3D transparency: fade the 3D models down to read the 2D footprint underneath.
  const opacity = document.createElement("input");
  opacity.type = "range";
  opacity.min = "0.1";
  opacity.max = "1";
  opacity.step = "0.05";
  opacity.value = "1";
  opacity.title = "Fade 3D models to see the 2D footprint underneath";
  opacity.addEventListener("input", () => layers.setOpacity(Number(opacity.value)));

  const wire = makeToggle("Wireframe", (on) => layers.setWireframe(on));

  // 3D-on-top: when ON, models always draw over the terrain (never cut by hills or a
  // mismatched DEM). When OFF (default) the terrain occludes 3D realistically, so a model
  // sitting below grade or behind a ridge is hidden - useful for judging real alignment.
  const onTop = makeToggle("3D on top", (on) => {
    viewer.scene.globe.depthTestAgainstTerrain = !on;
    viewer.scene.requestRender();
  }, true); // on by default - matches the viewer's depthTestAgainstTerrain=false default
  onTop.title =
    "Draw 3D models over the terrain instead of letting the terrain cut through them";

  // True orthographic top-down toggle (no perspective distortion for alignment).
  const topDown = document.createElement("button");
  topDown.className = "tool-btn";
  topDown.textContent = "Top-down";
  topDown.title = "Orthographic plan view - toggling off restores perspective";
  let ortho = false;
  topDown.addEventListener("click", () => {
    ortho = !ortho;
    topDown.classList.toggle("active", ortho);
    layers.topDown(ortho);
  });

  const flyTo = makeButton("Fly to data", () => layers.flyToVisible());

  // Go to a coordinate. Accepts either:
  //   "lat, lon"  - e.g. -22.6, 116.5 (the Google Maps format; paste one straight in)
  //   "E N"       - MGA easting northing in the zone selected in the Grid dropdown
  // Which one you typed is inferred from magnitude: a lat/long can't exceed 180, and an
  // MGA easting/northing is always far larger.
  //
  // The zone comes from $mgaZone. It used to be hardcoded to EPSG:28350 (GDA94 MGA zone
  // 50), so entering an SOE (zone 54) coordinate flew to the wrong place with no error.
  const goto = document.createElement("input");
  goto.type = "text";
  goto.className = "goto-input";
  const refreshGotoHint = (): void => {
    const z = $mgaZone.get();
    goto.placeholder = `Go to: lat, lon  or  E N (MGA${z})`;
    goto.title =
      `Enter a lat/long (e.g. -22.6, 116.5) or an MGA Zone ${z} easting northing ` +
      `(e.g. 470000 7495000). The zone follows the Grid selector.`;
  };
  refreshGotoHint();
  $mgaZone.subscribe(refreshGotoHint);
  const doGoto = async () => {
    const nums = goto.value.trim().split(/[ ,]+/).map(Number).filter((v) => !Number.isNaN(v));
    if (nums.length < 2) return;
    const [a, b] = nums;
    try {
      if (Math.abs(a) > 180 || Math.abs(b) > 180) {
        // a=easting, b=northing, in the currently selected MGA zone.
        const { lon, lat } = await api.toLonLat(a, b, epsgForZone($mgaZone.get()));
        flyToLonLat(viewer, lon, lat, 4000);
      } else {
        flyToLonLat(viewer, b, a, 4000); // "lat, lon" -> lon=b, lat=a
      }
    } catch (err) {
      toast(`Go to failed: ${(err as Error).message}`, "warn");
    }
  };
  goto.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void doGoto();
  });

  bar.append(plan, labelled("Base", base), labelled("Terrain", terrainSel), labelled("Grid", crsSel), labelled("Transparency", opacity), wire, onTop, topDown, flyTo, goto);
  container.append(bar);
}

function labelled(text: string, el: HTMLElement): HTMLElement {
  const wrap = document.createElement("label");
  wrap.className = "tool";
  wrap.append(Object.assign(document.createElement("span"), { textContent: text }), el);
  return wrap;
}

function makeButton(text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = "tool-btn";
  b.textContent = text;
  b.addEventListener("click", onClick);
  return b;
}

function makeToggle(text: string, onChange: (on: boolean) => void, checked = false): HTMLElement {
  const cb = document.createElement("input");
  cb.type = "checkbox";
  cb.checked = checked;
  cb.addEventListener("change", () => onChange(cb.checked));
  return labelled(text, cb);
}
